import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { supabase } from '@/lib/supabase';
import { UserPlus, ArrowLeft, CheckCircle } from 'lucide-react';

export function Register() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validações
    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      setLoading(false);
      return;
    }

    if (!fullName.trim()) {
      setError('Por favor, informe seu nome completo');
      setLoading(false);
      return;
    }

    try {
      // 1. Criar usuário no Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
        },
      });

      if (authError) throw authError;

      if (!authData.user) {
        throw new Error('Erro ao criar usuário');
      }

      // 2. Criar registro na tabela users
      const { error: userError } = await supabase.from('users').insert({
        id: authData.user.id,
        email: authData.user.email!,
        full_name: fullName,
        is_active: false, // Inativo até aprovação
      });

      if (userError) throw userError;

      // 3. Fazer logout automático (usuário precisa ser aprovado primeiro)
      await supabase.auth.signOut();

      // Mostrar mensagem de sucesso
      setSuccess(true);
    } catch (err: any) {
      console.error('Erro ao criar conta:', err);
      if (err.message?.includes('already registered')) {
        setError('Este e-mail já está cadastrado');
      } else {
        setError(err.message || 'Erro ao criar conta. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-chart-4/10/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-chart-4" />
            </div>
            <CardTitle className="text-2xl font-bold text-foreground">
              Conta Criada com Sucesso!
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-accent/10/30 border border-accent rounded-sm">
                <p className="text-sm text-accent">
                  <strong>Aguarde aprovação do administrador</strong>
                </p>
                <p className="text-xs text-accent mt-2">
                  Sua conta foi criada e enviada para aprovação. Você receberá um e-mail em{' '}
                  <strong>{email}</strong> quando um administrador aprovar seu acesso.
                </p>
              </div>

              <div className="space-y-2 text-sm text-muted-foreground">
                <p>📧 Verifique seu e-mail (inclusive spam/lixo eletrônico)</p>
                <p>⏱️ Aprovação geralmente leva até 24 horas</p>
                <p>🔒 Após aprovação, você poderá fazer login</p>
              </div>

              <Button
                className="w-full"
                onClick={() => navigate('/login')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar para Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-3xl font-bold text-accent">Criar Conta</CardTitle>
          <p className="text-muted-foreground">Preencha os dados para solicitar acesso</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="fullName">
                Nome Completo
              </label>
              <Input
                id="fullName"
                type="text"
                placeholder="João da Silva"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="email">
                E-mail
              </label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="password">
                Senha
              </label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
              <p className="text-xs text-muted-foreground">Mínimo 6 caracteres</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="confirmPassword">
                Confirmar Senha
              </label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10/30 border border-destructive rounded-sm p-3">
                {error}
              </div>
            )}

            {/* Info sobre aprovação */}
            <div className="p-3 bg-secondary/10/30 border border-secondary rounded-sm">
              <p className="text-xs text-secondary-foreground">
                <UserPlus className="w-4 h-4 inline mr-1" />
                <strong>Importante:</strong> Sua conta será criada e enviada para aprovação de
                um administrador. Você receberá um e-mail quando for aprovado.
              </p>
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Criando conta...' : 'Criar Conta'}
            </Button>

            {/* Link para voltar ao login */}
            <div className="mt-4 text-center text-sm">
              <span className="text-muted-foreground">Já tem uma conta? </span>
              <Link to="/login" className="text-accent hover:text-accent font-medium">
                Fazer login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
